import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import './Modal.css';
import { DriverTransaction, TransactionType } from '../../types/drivers';
import { driversService } from '../../services/driversService';

interface ViewAllDriverTransactionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  driverId: number;
  driverName: string;
}

const ViewAllDriverTransactionsModal: React.FC<ViewAllDriverTransactionsModalProps> = ({
  isOpen,
  onClose,
  driverId,
  driverName
}) => {
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState<DriverTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const pageSize = 20;

  useEffect(() => {
    if (isOpen) {
      loadTransactions(0, true);
    }
  }, [isOpen, driverId]);

  const loadTransactions = async (pageNum: number, reset: boolean = false) => {
    setLoading(true);
    try {
      const response = await driversService.getDriverTransactions(driverId, pageNum, pageSize);

      if (response.success && response.data) {
        if (reset) {
          setTransactions(response.data.transactions);
        } else {
          setTransactions(prev => [...prev, ...response.data!.transactions]);
        }
        setHasMore(response.data.hasMore);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShowMore = () => {
    if (!loading && hasMore) {
      loadTransactions(page + 1, false);
    }
  };

  const handleDeleteTransaction = async (transactionId: number) => {
    if (!window.confirm(t('driverDetails.confirmDeleteTransaction'))) {
      return;
    }

    try {
      const response = await driversService.deleteDriverTransaction(transactionId);
      if (response.success) {
        setSuccessMessage(t('driverDetails.transactionDeleted'));
        setTimeout(() => setSuccessMessage(''), 3000);
        loadTransactions(0, true);
      } else {
        throw new Error(response.error || 'Failed to delete transaction');
      }
    } catch (error) {
      console.error('Failed to delete transaction:', error);
      alert(t('driverDetails.deleteTransactionError'));
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString('en-US') + ' UZS';
  };

  const getTransactionTypeStyle = (type: TransactionType) => {
    if (type === TransactionType.FILL) {
      return {
        color: '#10b981',
        backgroundColor: '#ecfdf5',
        border: '1px solid #10b981'
      };
    }
    return {
      color: '#ef4444',
      backgroundColor: '#fef2f2',
      border: '1px solid #ef4444'
    };
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('modals.viewAllDriverTransactions.title')}
      maxWidth="900px"
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {successMessage && (
          <div className="success-message" style={{ marginBottom: '16px' }}>
            {successMessage}
          </div>
        )}

        {transactions.length === 0 && !loading ? (
          <p style={{ textAlign: 'center', color: '#6b7280', padding: '40px 20px' }}>
            {t('driverTransactions.noTransactions')}
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {transactions.map((transaction) => (
              <div
                key={transaction.id}
                style={{
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  opacity: transaction.isDeleted ? 0.5 : 1,
                  textDecoration: transaction.isDeleted ? 'line-through' : 'none',
                  position: 'relative'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span
                      style={{
                        padding: '4px 12px',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: 600,
                        ...getTransactionTypeStyle(transaction.transactionType)
                      }}
                    >
                      {transaction.transactionType === TransactionType.FILL
                        ? t('driverTransactions.fill')
                        : t('driverTransactions.withdraw')}
                    </span>
                    <span style={{ fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                      {formatAmount(transaction.amount)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>
                      {formatDate(transaction.createdAt)}
                    </span>
                    {!transaction.isDeleted && (
                      <button
                        onClick={() => handleDeleteTransaction(transaction.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#ef4444',
                          fontSize: '18px',
                          lineHeight: '1'
                        }}
                        title={t('driverDetails.deleteTransaction')}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
                <div
                  style={{ fontSize: '14px', color: '#374151', marginTop: '8px' }}
                  dangerouslySetInnerHTML={{ __html: transaction.descriptionHtml }}
                />
              </div>
            ))}
          </div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
            {t('modals.viewAllDriverTransactions.loading')}
          </div>
        )}

        {!loading && hasMore && transactions.length > 0 && (
          <button
            onClick={handleShowMore}
            style={{
              width: '100%',
              padding: '12px',
              marginTop: '16px',
              backgroundColor: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              color: '#374151'
            }}
          >
            {t('modals.viewAllDriverTransactions.showMore')}
          </button>
        )}

        {!loading && !hasMore && transactions.length > 0 && (
          <p style={{ textAlign: 'center', color: '#6b7280', padding: '20px', fontSize: '14px' }}>
            {t('modals.viewAllDriverTransactions.noMoreTransactions')}
          </p>
        )}
      </div>

      <div className="modal-actions" style={{ marginTop: '20px' }}>
        <button
          onClick={onClose}
          className="action-button"
          style={{ backgroundColor: '#6b7280' }}
        >
          {t('modals.viewAllDriverTransactions.close')}
        </button>
      </div>
    </Modal>
  );
};

export default ViewAllDriverTransactionsModal;
